'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useCart, addLine, removeLine, syncCartWithCatalog, type CartLine } from '@/lib/cart-store';
import { useCartDrawer, closeCartDrawer } from '@/lib/cart-drawer';
import { formatCentsClient } from '@/lib/format';
import { SHIPPING_SERVICE } from '@/lib/policies';
import { lineCap, overCap, preorderUnits } from '@/lib/cart-math';
import { CheckoutFallback, useCheckout } from './CheckoutFallback';

const UNDO_MS = 4000;

export function CartDrawer() {
  const open = useCartDrawer();
  const cart = useCart();
  const checkout = useCheckout(cart.lines);
  const [undo, setUndo] = useState<CartLine | null>(null);
  const undoTimer = useRef<number | null>(null);

  // Re-price the bag each time the drawer opens.
  useEffect(() => {
    if (open) void syncCartWithCatalog();
  }, [open]);

  // Lock body scroll when drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeCartDrawer();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function clearUndoTimer() {
    if (undoTimer.current !== null) {
      window.clearTimeout(undoTimer.current);
      undoTimer.current = null;
    }
  }

  function handleRemove(line: CartLine) {
    setUndo(line);
    clearUndoTimer();
    undoTimer.current = window.setTimeout(() => setUndo(null), UNDO_MS);
    removeLine(line.variantId);
  }

  function handleUndo() {
    if (!undo) return;
    const { qty, unavailable, ...rest } = undo;
    void unavailable;
    setUndo(null);
    clearUndoTimer();
    addLine(rest, qty);
  }

  const items = cart.lines;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeCartDrawer}
        aria-hidden={!open}
        className={[
          'fixed inset-0 bg-ink/40 z-[60]',
          'motion-safe:transition-opacity motion-safe:duration-200',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        ].join(' ')}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Cart"
        aria-hidden={!open}
        inert={!open}
        className={[
          'fixed top-0 right-0 h-full w-full sm:w-[420px] bg-bone z-[70]',
          'flex flex-col',
          'motion-safe:transition-[transform,visibility] motion-safe:duration-300',
          // Shadow only while open, and hidden once parked off-screen, so the
          // closed drawer cannot cast a haze along the viewport's right edge.
          open ? 'translate-x-0 shadow-2xl visible' : 'translate-x-full invisible',
        ].join(' ')}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-ink/10">
          <p className="text-xs uppercase tracking-[0.14em] text-ink/65">Your bag</p>
          <button
            onClick={closeCartDrawer}
            aria-label="Close cart"
            className="text-ink/60 hover:text-ink transition-colors p-1 -m-1"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6 6L18 18M6 18L18 6" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {cart.status === 'loading' ? (
            <p className="text-sm text-muted-strong uppercase tracking-[0.12em] text-xs">Loading…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-display tracking-[-0.01em] font-semibold text-xl text-ink mb-3">Your bag is empty</p>
              <p className="text-sm text-muted-strong mb-8 leading-relaxed">A discovery set is a soft place to start.</p>
              <Link
                href="/shop"
                onClick={closeCartDrawer}
                className="inline-block bg-plum text-white px-8 py-3 text-xs uppercase tracking-[0.12em] font-medium hover:bg-plum-deep transition-colors rounded-sm"
              >
                Browse the Collection
              </Link>
            </div>
          ) : (
            <ul className="space-y-5">
              {items.map((item) => (
                <li key={item.variantId} className="grid grid-cols-[64px_1fr_auto] gap-4 items-start">
                  <div className="relative aspect-square bg-white overflow-hidden">
                    {item.image && <Image src={item.image} alt="" fill sizes="64px" className="object-contain p-[6%]" />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-display tracking-[0.06em] font-semibold text-xs text-ink leading-tight">
                      {item.name}
                    </p>
                    {item.variantTitle && <p className="text-xs text-ink/60 mt-1">{item.variantTitle}</p>}
                    <p className="text-xs text-ink/60 mt-1">
                      Qty {item.qty}{preorderUnits(item) > 0 ? ` · ${preorderUnits(item)} pre-order` : ''}
                    </p>
                    <p className="text-xs text-ink font-numerals mt-1">{formatCentsClient(item.priceCents * item.qty)}</p>
                    {item.unavailable && <p className="text-xs text-ink font-medium mt-1">No longer available</p>}
                    {!item.unavailable && overCap(item) && (
                      <p className="text-xs text-ink font-medium mt-1">
                        Only {lineCap(item)} available. <Link href="/cart" onClick={closeCartDrawer} className="underline underline-offset-2">Lower the quantity</Link> to check out.
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemove(item)}
                    className="text-xs uppercase tracking-[0.12em] text-muted-strong hover:text-ink transition-colors py-1"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-ink/10 px-6 py-5 bg-bone">
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-xs text-ink/60 uppercase tracking-[0.12em]">Subtotal</span>
              <span className="font-numerals text-base text-ink">{formatCentsClient(cart.subtotalCents)}</span>
            </div>
            <p className="text-xs text-muted-strong mb-4">
              {SHIPPING_SERVICE}: {cart.shippingCents === 0 ? 'free' : formatCentsClient(cart.shippingCents)}. Tax calculated at checkout.
            </p>
            {checkout.error && <CheckoutFallback message={checkout.error.message} />}
            <button
              onClick={() => void checkout.go()}
              disabled={checkout.pending || cart.hasUnavailable || cart.hasOverCap || cart.subtotalCents <= 0}
              className="w-full bg-plum text-white py-4 text-xs uppercase tracking-[0.12em] font-medium hover:bg-plum-deep transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-sm"
            >
              {checkout.pending ? 'Redirecting…' : 'Checkout'}
            </button>
            <Link
              href="/cart"
              onClick={closeCartDrawer}
              className="block text-center mt-3 text-xs uppercase tracking-[0.12em] text-ink/60 hover:text-ink transition-colors"
            >
              View full cart
            </Link>
          </div>
        )}
      </aside>

      {/* Undo toast: pinned bottom-center, above the drawer. */}
      {undo && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-ink text-bone pl-5 pr-3 py-2.5 rounded-full shadow-2xl text-xs flex items-center gap-4 max-w-[92vw]"
        >
          <span className="truncate">
            Removed <span className="font-medium">{undo.name}</span>
          </span>
          <button
            onClick={handleUndo}
            className="uppercase tracking-[0.12em] text-xs font-medium px-3 py-1.5 rounded-full bg-bone/10 hover:bg-bone/20 transition-colors shrink-0"
          >
            Undo
          </button>
        </div>
      )}
    </>
  );
}
